import React, { useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toggleMapFilterToggle } from '@actions/map-actions';
import {
  fetchGroupsMapData,
  setGroupsLayerVisible,
  setGroupsLayerBoundariesVisible,
  toggleGroupLayerBoundaries,
  toggleGroupLayerLabels
} from '@actions/groups-actions';
import FilterCheckbox from '@shared/filter-checkbox';
import { getGroupIcon, getGroupLabelIcon } from '@utils/icon-utils';
import { getCurrentFilterToggles } from '@selectors/map-selector';
import BaseTrayFilter from './base-filter';
import './filter.scss';

const GroupsFilter = ({ config }) => {
  const dispatch = useDispatch();
  const filterEnabled = useSelector(state => getCurrentFilterToggles(state).group);
  const groups = useSelector(state => state.groups.list);
  const showLayerBoundaries = useSelector(state => state.groups.showLayerBoundaries);
  const showLayerLabels = useSelector(state => state.groups.showLayerLabels);

  const onToggleClick = useCallback(() => {
    if (filterEnabled) {
      dispatch(setGroupsLayerVisible(false));
    } else {
      // Enabling the filter:
      const hasBounds = groups.some(group => group.bounds);
      if (!hasBounds) {
        // If the groups doesn't have the bounds set, it means they were loaded first
        // from a non-map page, thus Google Maps code was not there to calculate bounds.
        // So, fetch them again, since we are on a map page now:
        dispatch(fetchGroupsMapData());
      }
      dispatch(setGroupsLayerVisible(true));  // Make the layer visible.
      dispatch(setGroupsLayerBoundariesVisible(true));  // Make boundaries visible (but not labels).
    }
    dispatch(toggleMapFilterToggle('group'));
  }, [dispatch, filterEnabled, groups]);

  const toggleBoundaries = useCallback(() => {
    dispatch(toggleGroupLayerBoundaries(!showLayerBoundaries));
  }, [dispatch, showLayerBoundaries]);

  const toggleLabels = useCallback(() => {
    dispatch(toggleGroupLayerLabels(!showLayerLabels));
  }, [dispatch, showLayerLabels]);

  const renderContent = useCallback(() => {
    const labelIconStyle = { padding: '0 5px', height: '1rem', width: '1rem' };
    return (
      <div styleName="content-list-container">
        <div styleName="list-item">
          <div styleName="checkbox">
            <FilterCheckbox disabled={!filterEnabled} checked={showLayerBoundaries} onChange={toggleBoundaries} />
          </div>
          <img styleName="icon" src={getGroupIcon()} alt="" style={{ height: '1.5rem', width: '1.5rem' }} aria-hidden="true" />
          <div styleName="label">
            Show group boundaries
          </div>
        </div>
        <div styleName="list-item">
          <div styleName="checkbox">
            <FilterCheckbox disabled={!filterEnabled} checked={showLayerLabels} onChange={toggleLabels} />
          </div>
          <div styleName="icon">
            <img
              alt=""
              aria-hidden="true"
              src={getGroupLabelIcon()}
              style={!filterEnabled ? { ...labelIconStyle, opacity: '0.3' } : labelIconStyle}
            />
          </div>
          <div styleName="label">
            Show group labels
          </div>
        </div>
      </div>
    );
  }, [filterEnabled, showLayerBoundaries, showLayerLabels, toggleBoundaries, toggleLabels]);

  return (
    <BaseTrayFilter
      config={config}
      filterEnabled={filterEnabled}
      onToggleClick={onToggleClick}
      renderContent={renderContent}
    />
  );
};

GroupsFilter.propTypes = {
  config: PropTypes.object
};

export default memo(GroupsFilter);
